import type { ComponentPropsWithoutRef } from "react";

import { closestCorners, DndContext, type DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { ReorderHandle } from "src/base-components/ReorderHandle";

type SortableFieldsProps = {
  items: string[];
  children: React.ReactNode;
  onChangeOrder: (items: { oldIndex: number; newIndex: number }) => void;
  disabled?: boolean;
};

export const SortableFieldsContext: React.FC<SortableFieldsProps> = ({
  items,
  children,
  onChangeOrder,
  disabled,
}) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id as string);
      const newIndex = items.indexOf(over.id as string);

      onChangeOrder({
        oldIndex,
        newIndex,
      });
    }
  };

  return (
    <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <SortableContext
        disabled={disabled}
        items={items}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};

type SortableFieldProps<T extends React.ElementType = "div"> = {
  id: string;
  children: React.ReactNode;
  disabled?: boolean;
  as?: T;
} & Omit<
  ComponentPropsWithoutRef<T>,
  "id" | "children" | "disabled" | "as" | "style"
>;

export const SortableField = <T extends React.ElementType = "div">({
  id,
  children,
  disabled,
  as,
  ...rest
}: SortableFieldProps<T>) => {
  const { listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const Wrapper = (as ?? "div") as React.ElementType;

  return (
    <Wrapper ref={setNodeRef} style={style} {...rest}>
      <div className="flex items-start gap-x-2">
        {!disabled && (
          <div className="pt-1">
            <ReorderHandle
              color="text-gray-400"
              listeners={listeners}
              size="xs"
            />
          </div>
        )}
        <div className="min-w-0 max-w-full flex-1">{children}</div>
      </div>
    </Wrapper>
  );
};
